const importPublicKey = async (pem: string): Promise<CryptoKey> => {
  const cleaned = pem
    .replace(/-----BEGIN PUBLIC KEY-----/g, '')
    .replace(/-----END PUBLIC KEY-----/g, '')
    .replace(/\s+/g, '')

  const der = Uint8Array.from(atob(cleaned), c => c.charCodeAt(0))

  return crypto.subtle.importKey(
    'spki',
    der,
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['encrypt']
  )
}

const toBase64 = (buf: ArrayBuffer): string => {
  let binary = ''
  const bytes = new Uint8Array(buf)
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary)
}

// Encrypt a small string (< ~190 bytes) with RSA-OAEP-SHA256.
// For PIN-encryption in future payment flows.
export const encryptWithPublicKey = async (
  plaintext: string,
  pemPublicKey: string
): Promise<string> => {
  const key = await importPublicKey(pemPublicKey)
  const encoded = new TextEncoder().encode(plaintext)
  const cipher = await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, key, encoded)
  return toBase64(cipher)
}

/**
 * Hybrid RSA-OAEP + AES-256-GCM encryption for variable-length payloads.
 *
 * An ephemeral AES-256-GCM key is generated for each call. That key is
 * wrapped with the recipient's RSA public key (RSA-OAEP-SHA256). The
 * payload is then encrypted with AES-GCM using a random 12-byte IV.
 *
 * Wire format (base64 of concatenated bytes):
 *   [2 bytes BE: rsaLen][rsaLen bytes: RSA-wrapped AES key]
 *   [12 bytes: AES-GCM IV][n bytes: AES-GCM ciphertext + 16-byte auth tag]
 *
 * Backend decryption: see docs/encryption-and-sessions.md
 */
export const encryptPayload = async (
  plaintext: string,
  pemPublicKey: string
): Promise<string> => {
  const rsaKey = await importPublicKey(pemPublicKey)

  const aesKey = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt']
  )

  const iv = crypto.getRandomValues(new Uint8Array(12))
  const encoded = new TextEncoder().encode(plaintext)

  const [rawAesKey, ciphertext] = await Promise.all([
    crypto.subtle.exportKey('raw', aesKey),
    crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, encoded),
  ])

  const wrappedKey = await crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    rsaKey,
    rawAesKey
  )

  const rsa = new Uint8Array(wrappedKey)
  const gcm = new Uint8Array(ciphertext)
  const out = new Uint8Array(2 + rsa.length + 12 + gcm.length)
  out[0] = (rsa.length >> 8) & 0xff
  out[1] = rsa.length & 0xff
  out.set(rsa, 2)
  out.set(iv, 2 + rsa.length)
  out.set(gcm, 2 + rsa.length + 12)

  return toBase64(out.buffer)
}
